import CustomPaper from '../common/CustomPaper';
import React, { useState, useEffect, useMemo } from 'react';
import {
  Grid,
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
} from '@mui/material';
import useMergeWorkflowStore, { getMergeWorkflowStore } from './useMergeWorkflowStore';
import { send_component_save_request, send_request } from '../../utils/Request';
import { injectMetadataFields } from '../../utils/buildComponentSavePayload';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import '../WebhookImport/WebhookImport.css';
import { AnyInputComponent, OrOfAndInputComponent, AllInputComponent } from './Components';
import { IndividualComponentLoader } from '../../utils/ComponentLoadingSkeleton';

const MergeWorkflowTemplate = (props) => {
  const [hasToken, setHasToken] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updateState, setUpdateState] = useState(false);
  const [description, setDescription] = useState(null);
  const [status, setStatus] = useState(null);

  const store = useMemo(() => getMergeWorkflowStore(props.component_id), [props.component_id]);

  const selectedFunction = store((s) => s.data?.function);
  const storeData = store((s) => s.data);

  const forceUpdateState = () => {
    setUpdateState((prev) => !prev);
  };

  useEffect(() => {
    if (props.component_id === undefined) return;

    if (store.getState().loaded) {
      props.onComponentNameChanged(store.getState().name);
      props.onLastModifiedChanged(store.getState().lastModified);
      if (!store.getState().relatedComponents || store.getState().relatedComponents.length === 0) {
        send_request(
          'component-service/merge_workflow/components/' + props.component_id,
          '',
          {}
        ).then((response) => {
          if (response && response.data) {
            store.getState().setRelatedComponents(response.data);
          }
        });
      }
      setLoading(false);
      props.setChildTemplateLoaded(true);
      return;
    }

    setLoading(true);

    Promise.resolve('done')
      .then(() => {
        return send_request(
          `project-service/project/component/query/${props.component_id}/at-version/${props.version}`,
          '',
          {}
        );
      })
      .then((response) => {
        if (response && response.data) {
          const { components } = response.data;
          if (components) {
            store.getState().setComponentData(components[0]);
            store.setState({ loaded: true });
            props.onComponentNameChanged(store.getState().name);
            props.onComponentDescriptionChanged(components[0].description);
            props.onLastModifiedChanged(store.getState().lastModified);
            if (components[0].status !== 'AI_BUILD_GENERATED') {
              setStatus(components[0].status);
            }

            send_request(
              'component-service/merge_workflow/components/' + props.component_id,
              '',
              {}
            ).then((response) => {
              if (response && response.data) {
                store.getState().setRelatedComponents(response.data);
              }

              setLoading(false);
            });
          }
        } else {
          setLoading(false);
        }
        props.setChildTemplateLoaded(true);
      })
      .catch((err) => {
        setLoading(false);
        throw Error(err.message);
      });
  }, []);

  useEffect(() => {
    const componentName = useProjectStore.getState().componentName;
    if (componentName !== undefined) {
      store.getState().setName(componentName);
    }
    const unsub = useProjectStore.subscribe(
      (s) => s.componentName,
      (name) => {
        store.getState().setName(name);
      }
    );
    return unsub;
  }, []);

  if (!loading) {
    return (
      <CustomPaper className="componentPaper" style={{ padding: '24px' }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
            Merge Condition
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Define when this merge point should continue the workflow
          </Typography>
        </Box>

        <FormControl component="fieldset" sx={{ width: '100%', mb: 3 }}>
          <RadioGroup
            aria-label="merge condition"
            name="merge-condition"
            value={selectedFunction}
            onChange={(e) => {
              store.getState().setFunction(e.target.value);
              store.getState().setFunctionDefaultValue();
            }}
          >
            {[
              { value: 'all', title: 'Wait for all inputs', desc: 'Continue only when all connected components have completed', mb: 1.5 },
              { value: 'any', title: 'Wait for any input', desc: 'Continue when any one of the selected components completes', mb: 1.5 },
              { value: 'or_and', title: 'Custom combinations', desc: 'Continue when specific combinations of components complete', mb: 0 },
            ].map(({ value, title, desc, mb }) => (
              <Box
                key={value}
                onClick={() => {
                  store.getState().setFunction(value);
                  store.getState().setFunctionDefaultValue();
                }}
                sx={{
                  border: '1px solid',
                  borderColor: selectedFunction === value ? 'text.primary' : 'divider',
                  borderRadius: '6px',
                  p: 2,
                  mb,
                  cursor: 'pointer',
                  backgroundColor: selectedFunction === value ? 'background.subtle' : 'background.paper',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    borderColor: 'text.disabled',
                    backgroundColor: 'background.subtle',
                  },
                }}
              >
                <FormControlLabel
                  value={value}
                  control={<Radio size="small" sx={{ p: 0, mr: 1.5, color: 'text.secondary', '&.Mui-checked': { color: 'text.primary' } }} />}
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {desc}
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0, alignItems: 'flex-start' }}
                />
              </Box>
            ))}
          </RadioGroup>
        </FormControl>

        <Grid container spacing={2}>
          {selectedFunction === 'any' && <AnyInputComponent store={store} />}
          {selectedFunction === 'all' && <AllInputComponent store={store} />}
          {selectedFunction === 'or_and' && <OrOfAndInputComponent store={store} />}
        </Grid>
      </CustomPaper>
    );
  } else return <IndividualComponentLoader />;
};

export default MergeWorkflowTemplate;
